import {
  DEFAULT_CURRENCY_DECIMALS,
  DEFAULT_NUMBER_DECIMALS,
  DEFAULT_PERCENT_DECIMALS,
  FORMATTER_CURRENCY,
  FORMATTER_LOCALE,
  NULL_DISPLAY,
} from './formatters.constants';

const isInvalid = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

const toNumber = (v: unknown): number => Number(v);

/**
 * Formata valor monetário em R$ com separadores pt-BR. Default: 2 decimais.
 * Ex.: 1234.5 -> "R$ 1.234,50"; formatCurrency(38210.5, 0) -> "R$ 38.211"
 *
 * As casas são parâmetro pelo mesmo motivo do `formatNumber`: manchete e cartão de resumo mostram
 * receita inteira, e sem o parâmetro a tela voltava a montar `Intl.NumberFormat` por conta própria.
 */
export function formatCurrency(
  value: number | null | undefined,
  decimals: number = DEFAULT_CURRENCY_DECIMALS,
): string {
  if (isInvalid(value)) return NULL_DISPLAY;
  return new Intl.NumberFormat(FORMATTER_LOCALE, {
    style: 'currency',
    currency: FORMATTER_CURRENCY,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(toNumber(value));
}

/**
 * Formata número com separador de milhar pt-BR. Default: inteiro.
 * Ex.: 10000 -> "10.000"; formatNumber(12.34, 2) -> "12,34"
 */
export function formatNumber(
  value: number | null | undefined,
  decimals: number = DEFAULT_NUMBER_DECIMALS,
): string {
  if (isInvalid(value)) return NULL_DISPLAY;
  return new Intl.NumberFormat(FORMATTER_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(toNumber(value));
}

/**
 * Formata percentual. Assume valor já em "porcento" (12.3, não 0.123).
 * Omite as casas decimais quando o valor é inteiro.
 * Ex.: 50 -> "50%"; 12.345 -> "12,3%"
 */
export function formatPercent(
  value: number | null | undefined,
  decimals: number = DEFAULT_PERCENT_DECIMALS,
): string {
  if (isInvalid(value)) return NULL_DISPLAY;
  const num = new Intl.NumberFormat(FORMATTER_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(toNumber(value));
  return `${num}%`;
}

/**
 * Abrevia número grande com sufixo k/M. Separador decimal pt-BR.
 * Ex.: 1500 -> "1,5k"; 1_234_567 -> "1,23M"
 * Com `currency: true` prefixa "R$ ": 1500 -> "R$ 1,5k"
 */
export function formatAbbreviated(
  value: number | null | undefined,
  options: { currency?: boolean; decimals?: number } = {},
): string {
  if (isInvalid(value)) return NULL_DISPLAY;
  const num = toNumber(value);
  const abs = Math.abs(num);
  const prefix = options.currency ? 'R$ ' : '';

  const fmt = (n: number): string => {
    const decimals =
      options.decimals ?? (Number.isInteger(n) ? 0 : abs >= 1_000_000 ? 2 : 1);
    return new Intl.NumberFormat(FORMATTER_LOCALE, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(n);
  };

  if (abs >= 1_000_000) return `${prefix}${fmt(num / 1_000_000)}M`;
  if (abs >= 1_000) return `${prefix}${fmt(num / 1_000)}k`;
  return `${prefix}${fmt(num)}`;
}
